import { billingReservationApi } from "../../redux/apis/invoice-api";
import { generateUuid } from "../../utils/uuid";

// BILLING STOCK RESERVATION
// Every bill has its own billing session (a UUID) so another terminal can't
// sell the units this bill holds. Whenever the bill's items change, the whole
// cart goes to /add-or-update/commited-qty as the total quantity to hold per
// item + batch + rack — never a difference. The server replaces the session's
// reservations with that cart, or rejects all of it with a 409 and keeps the
// previous ones, so resending a cart is always safe.

export const roundQty = (qty) => Math.round(qty * 10000) / 10000;

// A quantity in a row's own unit, in base (unit) qty. Manual-batch rows already
// store base units (pack_qty * pack_size + loose_qty); auto-batch rows store
// billing-UOM units, so they need the billing base quantity applied. Batch-
// barcode rows are built the manual way, so they're base units even in auto
// batching.
const toRowBaseQty = (row, qty, isAutoBatch) => {
  if (!isAutoBatch || row?.is_scanned_batch) return qty;
  const baseQty = parseFloat(row?.billing_base_quantity || 0) || 0;
  return baseQty > 0 ? qty * baseQty : qty;
};

export const getRowUnitQty = (row, isAutoBatch) =>
  toRowBaseQty(row, parseFloat(row?.quantity || 0) || 0, isAutoBatch);

// What a row holds per bin, in base (unit) qty. Auto-allocated rows can merge
// several bins of one batch and keep the split in bin_allocations; every
// other row sits in a single bin (its own rack location).
export const getRowBinAllocations = (row, isAutoBatch) =>
  row?.bin_allocations?.length
    ? row.bin_allocations
    : [
        {
          item_rack_location: row?.item_rack_location,
          to_bin_id: row?.to_bin_id,
          quantity: getRowUnitQty(row, isAutoBatch),
        },
      ];

const cartLineKey = (line) =>
  [line.item_id, line.item_batch_number, line.item_rack_location].join("|");

// One entry per item + batch + rack with the lines' quantities added up,
// nothing-to-hold entries dropped, in a fixed order so equal carts compare equal.
const normalizeCart = (lines) => {
  const totals = new Map();
  lines.forEach((line) => {
    const key = cartLineKey(line);
    totals.set(key, {
      ...line,
      commited_qty: (totals.get(key)?.commited_qty ?? 0) + line.commited_qty,
    });
  });
  return [...totals.keys()]
    .sort()
    .map((key) => {
      const line = totals.get(key);
      return { ...line, commited_qty: roundQty(line.commited_qty) };
    })
    .filter((line) => line.commited_qty > 0);
};

// A row's cart lines: one per bin it takes stock from. Free units ride on the
// first bin, the same way splitLineByBin saves them. Stock is held against a
// rack location, so a bin without one can't be reserved and is left out
// rather than failing the whole cart.
const rowCartLines = (row, isAutoBatch) => {
  const freeQty = toRowBaseQty(row, parseFloat(row?.item_free_quantity) || 0, isAutoBatch);
  return getRowBinAllocations(row, isAutoBatch)
    .map((bin, binIndex) => ({
      item_id: row.item_id,
      item_batch_number: row?.item_batch_number ?? "",
      item_rack_location: bin?.item_rack_location ?? "",
      commited_qty: (parseFloat(bin?.quantity) || 0) + (binIndex === 0 ? freeQty : 0),
    }))
    .filter((line) => line.item_rack_location);
};

// The bill's items as the reservation API wants them. Store-added items don't
// come out of inventory stock, so they're left out.
export const buildReservationCart = (rows, { isAutoBatch = false } = {}) =>
  normalizeCart(
    (rows ?? [])
      .filter((row) => row?.item_id && !row?.is_store_added_item)
      .flatMap((row) => rowCartLines(row, isAutoBatch))
  );

const reservationsToCart = (reservations) =>
  normalizeCart(
    (reservations ?? []).map((reservation) => ({
      item_id: reservation?.item_id,
      item_batch_number: reservation?.item_batch_number ?? "",
      item_rack_location: reservation?.item_rack_location ?? "",
      commited_qty: parseFloat(reservation?.reserved_qty) || 0,
    }))
  );

const cartKey = (cart) => JSON.stringify(cart);
const EMPTY_CART_KEY = cartKey([]);

// No answer (offline, timed out) or a gateway that couldn't reach the server.
// The api interceptor gives answerless errors a response with status 0.
const TRANSIENT_STATUSES = [0, 408, 502, 503, 504];
const isTransientFailure = (response) =>
  !response || TRANSIENT_STATUSES.includes(response.status ?? 0);

const NETWORK_RETRY_DELAYS_MS = [1000, 2000, 4000];

// A draft saved mid-bill (to add a doctor or an address) is reopened on Edit
// Billing in the same tab and must carry on with its session, or the stock it
// already holds would count against it. Per tab, like the session itself.
const draftSessionKey = (sotId) => `billingSession:${sotId}`;

export const recallDraftSession = (sotId) => {
  if (!sotId) return null;
  try {
    return sessionStorage.getItem(draftSessionKey(sotId));
  } catch (error) {
    // Storage blocked: the draft starts a new session, which resends its cart
    return null;
  }
};

const storeDraftSession = (sotId, sessionId) => {
  try {
    if (sessionId) {
      sessionStorage.setItem(draftSessionKey(sotId), sessionId);
    } else {
      sessionStorage.removeItem(draftSessionKey(sotId));
    }
  } catch (error) {
    // Storage blocked: see recallDraftSession
  }
};

export const RESERVATION_IDLE_STATE = {
  status: "idle", // "idle" | "conflict" | "error"
  unavailableItems: [],
  activeReservations: [],
  expiresAt: null,
  errorMessage: "",
};

// One bill's reservation. Requests are sent one at a time and only the latest
// cart is sent next, so an older cart can never land after a newer one.
// sessionId / savedCart: a reopened draft's session and the items it was
// saved with; a new bill gets a fresh session and nothing saved.
export const createBillingReservation = ({
  sessionId,
  savedCart = [],
  post = billingReservationApi,
} = {}) => {
  let session = sessionId || generateUuid();
  // What the bill holds while it's saved — put back if it's left unsaved
  let keptCart = savedCart;
  // What the server holds for this session; null = not known (a resumed
  // session, or a request that may or may not have reached it)
  let confirmedKey = sessionId ? null : EMPTY_CART_KEY;
  // A cart that was rejected or failed — not resent until it changes or flush()
  let stalledKey = null;
  let desiredCart = [];
  let inFlight = false;
  let retryTimer = null;
  let networkFailures = 0;
  // Bumped when a new bill starts, so the old bill's answers are ignored
  let generation = 0;
  let state = RESERVATION_IDLE_STATE;
  let waiters = [];
  const listeners = new Set();

  const setState = (changes) => {
    state = { ...state, ...changes };
    listeners.forEach((listener) => listener(state));
  };

  const resolveWaiters = () => {
    const settled = waiters;
    waiters = [];
    settled.forEach((resolve) => resolve(state));
  };

  const clearRetry = () => {
    clearTimeout(retryTimer);
    retryTimer = null;
  };

  const handleFailure = (response, key) => {
    const body = response?.data;
    if (response?.status === 409) {
      // Nothing of this cart was applied; the session keeps active_reservations
      const activeReservations = body?.data?.active_reservations;
      confirmedKey = Array.isArray(activeReservations)
        ? cartKey(reservationsToCart(activeReservations))
        : null;
      stalledKey = key;
      networkFailures = 0;
      setState({
        status: "conflict",
        unavailableItems: body?.data?.unavailable_items ?? [],
        activeReservations: body?.data?.active_reservations ?? [],
        errorMessage: body?.message ?? "",
      });
      return;
    }
    if (isTransientFailure(response)) {
      confirmedKey = null;
      if (networkFailures < NETWORK_RETRY_DELAYS_MS.length) {
        // Resend whatever the latest cart is by then, same session
        retryTimer = setTimeout(() => {
          retryTimer = null;
          pump();
        }, NETWORK_RETRY_DELAYS_MS[networkFailures]);
        networkFailures += 1;
        return;
      }
    }
    stalledKey = key;
    networkFailures = 0;
    setState({
      status: "error",
      errorMessage: body?.message || "Stock could not be reserved for this bill",
    });
  };

  const send = async (cart, key) => {
    const sentGeneration = generation;
    inFlight = true;
    try {
      const response = await post({ billing_session_id: session, cart_items: cart });
      if (sentGeneration !== generation) return;
      // previous_reservations_expired needs nothing: the cart is reserved again
      const data = response?.data?.data ?? {};
      confirmedKey = key;
      stalledKey = null;
      networkFailures = 0;
      setState({
        status: "idle",
        unavailableItems: [],
        errorMessage: "",
        activeReservations: data.active_reservations ?? [],
        expiresAt: data.expires_at ?? null,
      });
    } catch (error) {
      if (sentGeneration !== generation) return;
      handleFailure(error?.response, key);
    } finally {
      if (sentGeneration === generation) {
        inFlight = false;
        pump();
      }
    }
  };

  // Sends the latest cart when it isn't what the server holds, otherwise
  // settles anyone waiting in flush()
  function pump() {
    if (inFlight || retryTimer) return;
    const key = cartKey(desiredCart);
    if (key !== confirmedKey && key !== stalledKey) {
      send(desiredCart, key);
      return;
    }
    if (key === confirmedKey && state.status !== "idle") {
      setState({ status: "idle", unavailableItems: [], errorMessage: "" });
    }
    resolveWaiters();
  }

  const startNewBill = () => {
    generation += 1;
    clearRetry();
    inFlight = false;
    session = generateUuid();
    keptCart = [];
    desiredCart = [];
    confirmedKey = EMPTY_CART_KEY;
    stalledKey = null;
    networkFailures = 0;
    setState(RESERVATION_IDLE_STATE);
    resolveWaiters();
  };

  return {
    get sessionId() {
      return session;
    },
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    // The bill's items changed: this cart is the one to hold now
    sync: (cart) => {
      desiredCart = cart;
      pump();
    },
    // Resolves with the state once the latest cart has been answered. A cart
    // that was rejected or failed is tried again first — stock may be back.
    flush: () => {
      stalledKey = null;
      networkFailures = 0;
      if (retryTimer) clearRetry();
      const settled = new Promise((resolve) => waiters.push(resolve));
      pump();
      return settled;
    },
    // Save answered 409 (stock gone or reservations expired). What the server
    // holds is unknown now, so the cart is resent on the next change or save.
    reportSaveConflict: (body) => {
      confirmedKey = null;
      stalledKey = cartKey(desiredCart);
      setState({
        status: "conflict",
        unavailableItems: body?.data?.unavailable_items ?? [],
        errorMessage: body?.message ?? "",
      });
    },
    // The bill is saved; the next one gets a new session. A draft keeps this
    // one (and the stock it holds) for when it's reopened.
    billSaved: ({ sotId, isDraft }) => {
      if (sotId) storeDraftSession(sotId, isDraft ? session : null);
      startNewBill();
    },
    // The bill is left without saving: a new bill hands back everything it
    // holds (cart_items: []); a reopened draft goes back to its saved items.
    // After a save the session is a new, empty one, so nothing is sent.
    leave: () => {
      desiredCart = keptCart;
      pump();
    },
  };
};
